import type { Game } from '../types/Game'
import { GameListKey } from '../types/composables/UseGameList'
import type { UseGameTileReturn } from '../types/composables/UseGameTile'
import { useGameJackpotBindings } from './useGameJackpotBindings'
import {
    computed, inject, ref, watch,
    useDayjs, useI18n,
} from '#imports'
import type { Ref } from '#imports'

/**
 * Resolves the reactive state a single GameTile needs from the list-scoped
 * context set up by {@link useGameList}. Must be called inside a component
 * that descends from a `useGameList` provider — throws otherwise.
 *
 * Returns the same flat shape that standalone `useGameJackpot` /
 * `useGameOpeningHours` / `usePromotionGames` expose, so GameTile template
 * bindings don't change. The difference is that no per-tile `useAsyncData`,
 * `useCountdown`, or `useTimeoutFn` is created — all timers and queries
 * live once at the list scope.
 *
 * @param game - Reactive reference to the game rendered by this tile.
 *
 * @example
 * ```vue
 * <script setup lang="ts">
 * const props = defineProps<{ game: Game }>()
 * const game = toRef(props, 'game')
 * const { jackpot, opensIn, isClosed, gameHasSpins } = useGameTile(game)
 * </script>
 * ```
 */
export const useGameTile = (game: Ref<Game | undefined>): UseGameTileReturn => {
    const list = inject(GameListKey)
    if (!list) {
        throw new Error('useGameTile() must be used within a useGameList() provider scope.')
    }

    const { jackpotsQuery, promotionQuery, now } = list
    const { gamesJackpots } = jackpotsQuery

    const { t } = useI18n()
    const dayjs = useDayjs()

    const {
        jackpot, superJackpot, megaprize,
        jackpotTypeText, isBingoSlot, jackpotsLoaded,
    } = useGameJackpotBindings(game, gamesJackpots)

    const isOpen = ref(game.value?.opening_hours?.is_open ?? false)

    // Drives the isOpen flip when the shared clock passes opens_at — without
    // this, tiles render "opens in 0:00" forever and stay disabled.
    watch(
        [now, () => game.value?.opening_hours?.opens_at],
        ([nowValue, opensAtRaw]) => {
            if (!opensAtRaw) return
            isOpen.value = dayjs(opensAtRaw).valueOf() <= nowValue.valueOf()
        },
        { immediate: true },
    )

    const remainingSeconds = computed(() => {
        const opensAt = game.value?.opening_hours?.opens_at
        if (!opensAt) return 0
        const diff = dayjs(opensAt).valueOf() - now.value.valueOf()
        return Math.max(0, Math.floor(diff / 1000))
    })

    const opensIn = computed<string>(() => {
        if (isOpen.value || remainingSeconds.value <= 0) return ''

        const minutes = (remainingSeconds.value / 60) | 0
        const seconds = String(remainingSeconds.value % 60).padStart(2, '0')

        return `${t('game.gameTile.opens_in')} ${minutes}:${seconds}`
    })

    const opensAt = computed<string | null>(() => {
        if (isOpen.value) return null
        const opensAtRaw = game.value?.opening_hours?.opens_at
        if (!opensAtRaw) return null
        return `${t('game.gameTile.opens_at')} ${dayjs(opensAtRaw).tz().format('LT')}`
    })

    const opensSoon = computed<string | boolean | null>(() => {
        if (isOpen.value) return null

        const opensAtRaw = game.value?.opening_hours?.opens_at
        if (!opensAtRaw) return null

        const diff = dayjs(opensAtRaw).valueOf() - now.value.valueOf()
        return diff > 0 && diff < dayjs.duration({ minutes: 30 }).asMilliseconds()
    })

    const isClosed = computed<string | boolean | null>(() => {
        if (isOpen.value) return false
        return opensSoon.value ? opensIn.value : opensAt.value
    })

    const isDisabled = computed<boolean>(() => {
        return !game.value?.is_active || !!isClosed.value
    })

    const isPlayable = computed<boolean>(() => {
        return !isClosed.value && !isDisabled.value
    })

    return {
        jackpot,
        superJackpot,
        megaprize,
        jackpotTypeText,
        isBingoSlot,
        jackpotsLoaded,

        isOpen,
        opensIn,
        opensAt,
        opensSoon,
        isClosed,
        isDisabled,
        isPlayable,

        gameHasSpins: promotionQuery.gameHasSpins,
        gameHasTickets: promotionQuery.gameHasTickets,
        gameHasPromotion: promotionQuery.gameHasPromotion,
        gameHasInGameMessage: promotionQuery.gameHasInGameMessage,
    }
}
